import { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { useParams } from "react-router-dom";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import IconButton from "../../../../components/common/form/IconButton";
import { FaLink, FaSearch, FaTimes } from "react-icons/fa";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { useGetManagementCompanyByIdQuery, useUpdateManagementCompanyDetailApiMutation } from "../managementCompany";
import CommonSelectorModal from "../../../../components/common/form/CommonSelectorModal";
import { useSearchThirdPartyProviderApiQuery } from "../../thirdPartyProvider/thirdPartyProvider";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";

interface props {
    isReadOnly?: boolean;
}

const ManagementCompanyDetailInfo: FC<props> = ({ isReadOnly }) => {
    const { companyId } = useParams();
    const [companysData, setCompanyData] = useState<any>({});

    const { data: companyData } = useGetManagementCompanyByIdQuery(companyId);
    const { data: enums } = useGetEnumsQuery("");
    const [showButtons, setShowButtons] = useState<boolean>(false);
    const [updateManagementCompanyApi, updateManagementCompanyApiResponse] = useUpdateManagementCompanyDetailApiMutation()
    const iconButtonStyle = "fa cursor-pointer text-highlight-0 absolute left-auto right-5 top-6";
    const [selectedRow, setSelectedRow] = useState<any>();
    const [showProviderModal, setShowProviderModal] = useState<boolean>(false);

    useEffect(() => {
        if (companyData) {
            const obj = companyData;
            setCompanyData({
                ...obj,
                overrideAssociationStatus: obj?.overrideAssociationStatus
                    ? {
                        value: obj.overrideAssociationStatus,
                        label: getEnumDisplayNameByConstant(
                            enums?.["AssociationStatus"],
                            obj.overrideAssociationStatus
                        ),
                    }
                    : null,
                hoaThirdPartyProvider: obj?.hoaThirdPartyProvider?.name
            });
            setSelectedRow({ id: obj?.hoaThirdPartyProvider?.id })
        }
    }, [companyData, enums]);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setCompanyData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
        setShowButtons(true);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        let obj = {
            overrideAssociationStatus: companysData?.overrideAssociationStatus ? companysData?.overrideAssociationStatus?.value : null,
            hoaThirdPartyProvider: selectedRow?.id
                ? { id: selectedRow?.id }
                : null,
            name: companysData?.name,
        }
        const UpdateSystemProperty = async () => {
            await updateManagementCompanyApi({
                companyId: companyId,
                requestBody: obj,
            }).unwrap();
        };
        await handleRTKQuery(UpdateSystemProperty, () => {
            setShowButtons(false);
        });
    }

    const handCancel = async (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault(); // Handle both form and button clicks safely
        setShowButtons(false); // Hide buttons on cancel
    };


    const handleClearThirdPartyProvider = () => {
        setCompanyData((prevData: any) => ({
            ...prevData,
            hoaThirdPartyProvider: '',
        }));
        setSelectedRow({});
    };

    const columns: DataTableColumn[] = [
        {
            label: "Name",
            accessor: "name",
            filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''} />
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }],

        }
    ]

    const handleOnClick = (e: any) => {
        e.preventDefault();
        setCompanyData({
            ...companysData,
            hoaThirdPartyProvider: selectedRow.name
        })
        setShowButtons(true)
        setShowProviderModal(false)
    }


    return (
        <div className="bg-text-1 shadow-[0_3px_10px_#0003] text-sm rounded-md">
            <Form
                onSubmit={handleSubmit}
                fields={
                    <>
                        <div className="flex justify-between items-center  border-b border-[rgba(0,0,0,0.1)] p-2">
                            <h5 className="font-semibold text-surface-0">Management Company Detail
                            </h5>
                            {showButtons && isReadOnly === false && (
                                <ButtonTray
                                    buttons={[
                                        {
                                            buttonProps: {
                                                btnText: "Cancel",
                                                onClick: () => handCancel(),
                                                type: "close",
                                            },
                                            buttonType: "button"
                                        },
                                        {
                                            buttonProps: {
                                                btnText: "Submit",
                                                type: "submit",
                                                isLoading: (updateManagementCompanyApiResponse.isLoading),
                                            },
                                            buttonType: "button",
                                        }
                                    ]}
                                    align="end"
                                    gap={2}
                                />
                            )}
                        </div>
                        <div className={'p-2'}>
                            <InputField
                                name="name"
                                label="Name"
                                value={companysData?.name || ""}
                                onChange={handleOnChange}
                                required
                                placeholder="Enter Name..."
                                disabled={isReadOnly}

                            />
                            <InputField
                                name="hoaThirdPartyProvider"
                                label="Third Party Provider"
                                onChange={handleOnChange}
                                value={companysData?.hoaThirdPartyProvider || ""}
                                type="text"
                                placeholder="Enter Third Party Provider..."
                                disabled={isReadOnly}
                                icon={<IconButton
                                    icon={
                                        <FaLink
                                            onClick={() => {
                                                if (hasPermission(["HOATPP-R"]) && selectedRow?.id) {
                                                    window.open(`/third-party-providers/${selectedRow.id}`, '_blank');
                                                }
                                            }}
                                        />
                                    }

                                    className={`text-sm  ${companysData?.hoaThirdPartyProvider ? 'text-highlight-4' : 'text-surface-4 cursor-default'
                                        }`}
                                    title={"Create Third Party Provider"}
                                />}
                                onClick={() => {
                                    if (!isReadOnly) {
                                        setShowProviderModal(true);
                                    }
                                }}
                                className="cursor-pointer"
                                inputIcon={
                                    companysData?.hoaThirdPartyProvider ? (
                                        <FaTimes
                                            className={iconButtonStyle}
                                            title="Clear Data"
                                            onClick={() => {
                                                handleClearThirdPartyProvider();
                                                setShowButtons(true);
                                            }}
                                        />

                                    ) :
                                        <FaSearch
                                            className={iconButtonStyle}
                                            title="Add Provider Detail"
                                            onClick={() => {
                                                if (!isReadOnly) {
                                                    setShowProviderModal(true);
                                                }
                                            }}
                                        />

                                }
                            />
                            <SelectField
                                placeholder="Select Status..."
                                value={companysData?.overrideAssociationStatus || null}
                                options={createOptionListForSelectTag(enums?.["AssociationStatus"], "value", "constant")}
                                onChange={(selectedOption) => handleOnChange({ name: "overrideAssociationStatus", value: selectedOption })}
                                label="Override Association Status"
                            />
                        </div>
                    </>
                }
            />
            {showProviderModal &&
                <CommonSelectorModal
                    title="Third Party Selector"
                    columns={columns}
                    data={useSearchThirdPartyProviderApiQuery}
                    show={showProviderModal}
                    onSubmit={handleOnClick}
                    setShow={setShowProviderModal}
                    setSelectedRow={setSelectedRow}
                />
            }
        </div>
    );
};

export default ManagementCompanyDetailInfo;